const SVG_MIME_TYPE = "image/svg+xml";
const PNG_MIME_TYPE = "image/png";
const RASTERIZED_LOGO_MAX_DIMENSION_PX = 1024;

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }

      reject(new Error("Unable to read the selected logo."));
    };
    reader.onerror = () => reject(new Error("Unable to read the selected logo."));
    reader.readAsDataURL(file);
  });

const loadImage = (source: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load the selected logo."));
    image.src = source;
  });

const getSvgSource = (svgDataUrl: string): string | null => {
  const commaIndex = svgDataUrl.indexOf(",");
  if (commaIndex === -1) return null;

  const metadata = svgDataUrl.slice(0, commaIndex);
  const data = svgDataUrl.slice(commaIndex + 1);

  try {
    return /;base64/i.test(metadata) ? atob(data) : decodeURIComponent(data);
  } catch {
    return null;
  }
};

const normalizeSvgDataUrl = (logoDataUrl: string): string => {
  const commaIndex = logoDataUrl.indexOf(",");
  if (commaIndex === -1) return logoDataUrl;

  const metadata = logoDataUrl.slice(0, commaIndex);
  const data = logoDataUrl.slice(commaIndex + 1);
  const encoding = /;base64/i.test(metadata) ? ";base64" : "";

  return `data:${SVG_MIME_TYPE}${encoding},${data}`;
};

const getSvgAspectRatio = (svgDataUrl: string): number | null => {
  const svgSource = getSvgSource(svgDataUrl);
  const openingSvgTag = svgSource?.match(/<svg\b[^>]*>/i)?.[0];
  if (!openingSvgTag) return null;

  const viewBoxValue = openingSvgTag.match(
    /\bviewBox\s*=\s*["']([^"']+)["']/i
  )?.[1];
  const viewBox = viewBoxValue
    ?.trim()
    .split(/[\s,]+/)
    .map(Number);

  if (
    viewBox?.length === 4 &&
    Number.isFinite(viewBox[2]) &&
    Number.isFinite(viewBox[3]) &&
    viewBox[2] > 0 &&
    viewBox[3] > 0
  ) {
    return viewBox[2] / viewBox[3];
  }

  const widthValue = openingSvgTag.match(/\bwidth\s*=\s*["']([^"']+)["']/i)?.[1];
  const heightValue = openingSvgTag.match(/\bheight\s*=\s*["']([^"']+)["']/i)?.[1];
  const width = widthValue?.trim().endsWith("%") ? NaN : Number.parseFloat(widthValue || "");
  const height = heightValue?.trim().endsWith("%") ? NaN : Number.parseFloat(heightValue || "");

  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
    ? width / height
    : null;
};

const getRasterizedLogoDimensions = ({
  image,
  svgDataUrl,
}: {
  image: HTMLImageElement;
  svgDataUrl: string;
}): { width: number; height: number } => {
  const intrinsicAspectRatio =
    image.naturalWidth > 0 && image.naturalHeight > 0
      ? image.naturalWidth / image.naturalHeight
      : 1;
  const aspectRatio = getSvgAspectRatio(svgDataUrl) || intrinsicAspectRatio;

  if (aspectRatio >= 1) {
    return {
      width: RASTERIZED_LOGO_MAX_DIMENSION_PX,
      height: Math.max(
        1,
        Math.round(RASTERIZED_LOGO_MAX_DIMENSION_PX / aspectRatio)
      ),
    };
  }

  return {
    width: Math.max(
      1,
      Math.round(RASTERIZED_LOGO_MAX_DIMENSION_PX * aspectRatio)
    ),
    height: RASTERIZED_LOGO_MAX_DIMENSION_PX,
  };
};

const rasterizeSvgAsPng = async (svgDataUrl: string): Promise<string> => {
  const image = await loadImage(svgDataUrl);
  const { width, height } = getRasterizedLogoDimensions({
    image,
    svgDataUrl,
  });
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to convert the selected SVG logo.");
  }

  // The released mobile app uses native image decoders that do not render SVG.
  // Keep transparency while producing an app-compatible PNG before upload.
  context.drawImage(image, 0, 0, width, height);
  const pngDataUrl = canvas.toDataURL(PNG_MIME_TYPE);

  if (!pngDataUrl.startsWith(`data:${PNG_MIME_TYPE};base64,`)) {
    throw new Error("Unable to convert the selected SVG logo.");
  }

  return pngDataUrl;
};

const isSvgLogo = (file: File): boolean =>
  file.type.toLowerCase() === SVG_MIME_TYPE ||
  file.name.toLowerCase().endsWith(".svg");

export const readAffinityProgramLogoFile = async (
  file: File
): Promise<string> => {
  const logoDataUrl = await readFileAsDataUrl(file);

  return isSvgLogo(file)
    ? rasterizeSvgAsPng(normalizeSvgDataUrl(logoDataUrl))
    : logoDataUrl;
};
